// Почему у человека в ведомости нет денег — словами и по отметкам сервера, а не по нулям.
//
// ## Зачем
//
// Проход по боевым данным глазами финдиректора 23.09.2026: у тех, кто отработал месяц, но без
// тарифа, в ячейках стоял прочерк с пояснением «расчёт ещё не проводился» — хотя пересчёт
// ничего бы не изменил, не хватало тарифа. У тех, у кого нет часов, стоял «0» с пояснением
// «ноль штатный: начисления равны официальной части» — неправда от начала до конца. Панель
// человека противоречила сама себе: сверху «требует разбора: тариф не заполнен», снизу «ноль
// штатный, в разбор такая строка не попадает».
//
// Причину знает сервер: он ставит человеку отметки («rate_missing:2», «hours_missing:1») и
// строке — пустые поля тарифа и часов. Здесь это читается ОДИН раз на все места экрана: ячейку,
// панель человека, панель строки. Три места со своими догадками однажды назовут три причины.
//
// ## Порядок причин — порядок того, что чинить первым
//
// Нет тарифа — начислять не от чего вовсе. Невозможная ставка и невозможная норма — расчёт идёт,
// но число неверное. Нет часов — последнее: это не ошибка данных, а незакрытый табель.

import type { PayrollEmployeeOut, PayrollRowOut } from '@/shared/api/endpoints/payroll';

export type ПричинаПустоты = 'нет тарифа' | 'ставка невозможна' | 'норма невозможна' | 'нет часов';

/** Коды отметок сервера без числа: «rate_missing:2» → «rate_missing». */
function кодыОтметок(человек: PayrollEmployeeOut): ReadonlySet<string> {
  return new Set(человек.flags.map((отметка) => отметка.split(':')[0] ?? отметка));
}

/**
 * У строки нет исходных данных для ставки. То же правило, что у сервера
 * (`payroll_calc._без_тарифа`): у каждого режима свои поля.
 */
export function строкаБезТарифа(строка: PayrollRowOut): boolean {
  if (строка.row_type === 'salary') {
    return false;
  }
  if (строка.rate_mode === 'direct') {
    return строка.rate === null;
  }
  if (строка.rate_mode === 'by_target') {
    return строка.target_amount === null || строка.target_amount === undefined;
  }
  return строка.period_amount === null || строка.norm_hours === null;
}

/**
 * Почему по человеку (или по его строке) нет денег. Пусто — причины среди известных нет, и
 * ноль, если он есть, правдивый.
 *
 * Строка задана — смотрим сначала на неё: у человека бывает две строки, и тариф пуст только в
 * одной. Не задана — на отметки человека целиком.
 */
export function причинаПустоты(
  человек: PayrollEmployeeOut,
  строка: PayrollRowOut | null,
): ПричинаПустоты | null {
  const отметки = кодыОтметок(человек);
  if (строка !== null ? строкаБезТарифа(строка) : отметки.has('rate_missing')) {
    return 'нет тарифа';
  }
  if (отметки.has('rate_implausible')) {
    return 'ставка невозможна';
  }
  if (отметки.has('norm_impossible')) {
    return 'норма невозможна';
  }
  const безЧасов =
    строка !== null
      ? строка.row_type !== 'salary' && !строка.is_excluded && строка.hours === null
      : отметки.has('hours_missing');
  return безЧасов ? 'нет часов' : null;
}

/** Причина словами: что случилось, почему здесь нет денег и что сделать. */
export const ТЕКСТ_ПРИЧИНЫ: Readonly<Record<ПричинаПустоты, string>> = {
  'нет тарифа':
    'Не посчитано: в назначении не заполнен тариф — ни суммы за период с нормой часов, ни ' +
    'ставки. Начислять не от чего, и пересчёт этого не изменит. Заполните тариф: кнопка ' +
    '«Заполнить тариф» в полосе над сеткой.',
  'ставка невозможна':
    'Ставка меньше 50 ₽ в час — так не бывает. Похоже, в сумму за период попало начисленное за ' +
    'месяц, а ставка на объекте задана прямо, в рублях за час. Поправьте тариф в назначении.',
  'норма невозможна':
    'Норма часов больше, чем часов в месяце: начислено во столько же раз меньше, чем следует. ' +
    'Похоже, это норма объекта, а не человека. Поправьте норму в назначении.',
  'нет часов':
    'Часов нет: в табеле за месяц у человека нет ни одной смены на этом объекте, а табель ещё ' +
    'открыт — программа не знает, не выходил он или смены просто не отмечены. Не выходил — ' +
    'закройте табель, и здесь встанет ноль. Выходил — отметьте смены в табеле.',
};

/** Прочерк вместо нуля: причина есть, и она делает число неизвестным, а не нулевым. */
export function неизвестноИз(причина: ПричинаПустоты | null): boolean {
  return причина === 'нет тарифа' || причина === 'нет часов';
}
